import { observer } from 'mobx-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { useMemo, useRef } from 'react';
import moment from 'moment';
import { COURSE_STATUS_COMPLETE, DATE_YMD, MSG } from '../../core/configs/constants';
import { convertVideoDuration } from '../../core/utils/common';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../core/utils/hook';
import classNames from 'classnames';
import { flowResult } from 'mobx';
import { ReactNotifications } from '../../components';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import './styles.scss';

const UnCompletedCourseTab = observer(({
    lstTrainingCourssAttending,
    lstTrainingCourseGroupedByTicketPlans,
    onFetchData
}) => {
    // other
    const navigate = useNavigate();

    // store
    const { modalStore: { openWarningModal }, userStore: { cancelAttendance, getUserInfoHeader, userInfoHeader } } = useStore();
    
    // ref
    const prevRef = useRef(null);
    const nextRef = useRef(null);

    // slide
    const slides = useMemo(() => {
        if (lstTrainingCourssAttending?.length > 0) {
            return lstTrainingCourssAttending.map(course => ({
                id: course.id,
                title: course.name,
                thumbnail: course.thumbnail,
                statusUserCourse: course.statusUserCourse,
                attendedDate: course.attendedDate,
                deadline: course.deadline,
                totalTimeCourse: course.totalTimeCourse,
                totalLecture: course.totalLecture,
                totalTimeJoinCourse: course.totalTimeJoinCourse,
                isAllowJoin: course.isAllowJoin,
                isCancelAttendance: course.isCancelAttendance,
                incentivePeriod: course.incentivePeriod
            })).sort((a, b) => new Date(b.attendedDate) - new Date(a.attendedDate));
        }
        return [];
    }, [lstTrainingCourssAttending]);

    const isExpired = useMemo(() => {
        if (userInfoHeader) {
            return moment(userInfoHeader.coursePeriodEnd).isBefore(moment(), 'days');
        }
    }, [userInfoHeader])

    // Group courses NOT YET attended (statusUserCourse null) by ticketplan.
    // Drop empty groups so no extra heading is shown.
    const notAttendGroups = useMemo(() => {
        if (!lstTrainingCourseGroupedByTicketPlans?.length) return [];
        return lstTrainingCourseGroupedByTicketPlans
            .map(group => ({
                ...group,
                courses: (group.courses || []).filter(
                    c => !c.statusUserCourse && c.statusUserCourse !== 0
                )
            }))
            .filter(group => group.courses.length > 0);
    }, [lstTrainingCourseGroupedByTicketPlans]);

    const totalNotAttend = useMemo(
        () => notAttendGroups.reduce((sum, g) => sum + g.courses.length, 0),
        [notAttendGroups]
    );

    // function
    const handleGoDoTest = (id) => {
        navigate(`/course/${id}/completion-test`);
    }

    const handleCancelAttendance = async (id) => {
        openWarningModal(MSG['cancel.attendance.update.confirm'], async () => {
            const res = await flowResult(cancelAttendance(id));
            if (res) {
                getUserInfoHeader();
                onFetchData && onFetchData();
                ReactNotifications('success', MSG['inform.success.cancel.attendance']);
            }
        })
    }
    
    return (
        <div className='un-completed-training-course-tab'>
            <div className='attending-training-course-container'>
                <div className='title'>受講中の指定講座</div>
                <div className='content'>
                    {
                        slides?.length > 0 && (
                            <div ref={prevRef} className='cursor-pointer flex-none'>
                                <img src='/images/icon-arrow-left.svg' />
                            </div>
                        )
                    }
                    {
                        slides?.length > 0 ? (
                            <Swiper
                                direction='horizontal'
                                modules={[Navigation]}
                                navigation={{
                                    prevEl: prevRef.current,
                                    nextEl: nextRef.current
                                }}
                                breakpoints={{
                                    320: {
                                        slidesPerView: 1
                                    },
                                    992: {
                                        slidesPerView: 2
                                    },
                                    1280: {
                                        slidesPerView: 3
                                    }
                                }}
                                onInit={(swiper) => {
                                    setTimeout(() => {
                                        if (swiper.params?.navigation) {
                                            if (typeof swiper.params?.navigation !== 'boolean') {
                                                swiper.params.navigation.prevEl = prevRef.current;
                                                swiper.params.navigation.nextEl = nextRef.current;
                                                swiper.navigation.init();
                                                swiper.navigation.update();
                                            }
                                        }
                                    });
                                }}
                                loop={true}
                                slidesPerView={3}
                                spaceBetween={30}
                                allowTouchMove={true}
                                className='carousel-course-training-container'
                            >
                                {slides.map((slide) => {
                                    return (
                                        <SwiperSlide key={slide.id}>
                                            <div className='slide-content pd-10'>
                                                <div className='slide-content-header d-flex justify-content-between align-items-center'>
                                                    <span className='attended-date'>{`受講開始日 : ${moment(slide.attendedDate).format(DATE_YMD)}`}</span>
                                                    <span className='deadline-date'>{`受講期限 : ${moment(slide.deadline).format(DATE_YMD)}`}</span>
                                                </div>
                                                <div className='slide-content-body'>
                                                    <div className='info d-flex flex-gap-5'>
                                                        <img src={slide?.thumbnail || '/images/img-1.png'} className='slide-body-thumbnail' alt='' />
                                                        <div className='slide-body-info'>
                                                            <div className='title line-clamp-2 cursor-pointer'
                                                                onClick={() => navigate(`/course/${slide.id}`)}>{slide.title}</div>
                                                            <div className='info d-flex align-items-center flex-gap-15'>
                                                                <div className='d-flex align-items-center flex-gap-5'>
                                                                    <img className='mg-0' src='/images/icon1.svg' alt=''
                                                                        style={{ height: '12px', width: '12px' }} />
                                                                    <span style={{ color: '#7F7F7F' }}>
                                                                        {convertVideoDuration(slide.totalTimeCourse)} 分</span>
                                                                </div>
                                                                <div className='d-flex align-items-center flex-gap-5'>
                                                                    <img src='/images/icon2.svg' className='mg-0' alt=''
                                                                        style={{ height: '12px', width: '12px' }} />
                                                                    {slide.totalLecture ?? 0} コンテンツ
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='progress-bar-container'>
                                                        <div className='progress-bar-total'>
                                                            <div
                                                                className='progress-bar-viewed'
                                                                // eslint-disable-next-line max-len
                                                                style={{ width: `${(slide.totalTimeJoinCourse/slide.totalTimeCourse*100).toFixed(0)}%` }}>
                                                            </div>
                                                        </div>
                                                        <div className='d-flex align-items-center justify-content-between'>
                                                            {/* eslint-disable-next-line max-len */}
                                                            <span>{`視聴進捗率 : ${(slide.totalTimeJoinCourse/slide.totalTimeCourse*100).toFixed(0)}%`}</span>
                                                            {
                                                                slide.incentivePeriod && (
                                                                    <div style={{ fontSize: '13px' }}>
                                                                        <span style={{ color: '#6F6F6F' }}>受講推奨期限 : </span>
                                                                        <span style={{ fontWeight: '500px', color: '#027DB4' }}>
                                                                            {moment(slide.incentivePeriod).format(DATE_YMD)}
                                                                        </span>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='slide-content-footer d-flex justify-content-between align-items-center mg-t-10'>
                                                    {
                                                        slide.statusUserCourse === COURSE_STATUS_COMPLETE.USER_COURSE_NOT_ATTENDED ||
                                                        slide.statusUserCourse === COURSE_STATUS_COMPLETE.USER_COURSE_ATTENDING ? (
                                                                <>
                                                                    {
                                                                        slide.isCancelAttendance ? (
                                                                            // eslint-disable-next-line max-len
                                                                            <button 
                                                                                type='button'
                                                                                onClick={() => handleCancelAttendance(slide.id)}
                                                                                className='second-btn cancel d-flex align-items-center flex-gap-5'>
                                                                                <span>受講をキャンセル</span>
                                                                            </button>
                                                                        ) : <div></div>
                                                                    }
                                                                    <button
                                                                        type='button'
                                                                        disabled={isExpired}
                                                                        onClick={() => navigate(`/course/${slide.id}`)}
                                                                        className={classNames('primary-btn',
                                                                            isExpired && 'cursor-pointer-none opacity-50')}>受講を続ける</button>
                                                                </>
                                                            ) : <></>
                                                    }
                                                    {
                                                        slide.statusUserCourse === COURSE_STATUS_COMPLETE.USER_COURSE_NOT_DO_TEST ? (
                                                            <>
                                                                <button
                                                                    onClick={() => navigate(`/course/${slide.id}`)}
                                                                    type='button'
                                                                    className={classNames('second-btn info d-flex align-items-center flex-gap-5')}>
                                                                    <span>詳細を見る</span>
                                                                </button>
                                                                <button
                                                                    type='button'
                                                                    className={classNames('primary-btn',
                                                                        isExpired && 'cursor-pointer-none opacity-50')}
                                                                    disabled={isExpired}
                                                                    onClick={() => handleGoDoTest(slide.id)}>確認テスト</button>
                                                            </>
                                                        ) : <></>
                                                    }
                                                </div>
                                            </div>
                                        </SwiperSlide>
                                    )
                                })}
                            </Swiper>
                        ) : <span className='d-flex justify-content-center align-items-center min-height-200'
                            style={{width: '100%'}}>受講中の指定講座はありません。</span>
                    }
                    {
                        slides?.length > 0 && (
                            <div ref={nextRef} className='cursor-pointer flex-none'>
                                <img src='/images/icon-arrow-right.svg' />
                            </div>
                        )
                    }
                </div>
            </div>
            <div className='not-attend-training-course-container'>
                <div className='title'>{`未受講の指定講座 (${totalNotAttend})`}</div>
                <div className='content'>
                    {
                        notAttendGroups.length > 0 ? (
                            notAttendGroups.map((group, gIdx) => (
                                <div key={group.id ?? gIdx} className='ticketplan-uncompleted-group'>
                                    <div className='ticketplan-uncompleted-group-header'>{group.name}</div>
                                    <div className='ticketplan-uncompleted-group-list'>
                                        {group.courses.map((course, cIdx) => (
                                            <UnCompletedSimpleItem
                                                key={`${group.id ?? gIdx}-${course.id ?? cIdx}`}
                                                course={course}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : <div className='text-center'>未受講の指定講座はありません。 </div>
                    }
                </div>
            </div>
        </div>
    )
});

const UnCompletedSimpleItem = observer(({ course }) => {
    const navigate = useNavigate();
    const goToDetail = () => navigate(`/course/${course.id}`);

    return (
        <div className='uncompleted-course-card' onClick={goToDetail}>
            <img
                className='uncompleted-course-card__thumb'
                src={course.thumbnail || '/images/img-1.png'}
                alt=''
            />
            <div className='uncompleted-course-card__body'>
                <div className='uncompleted-course-card__title line-clamp-1'>{course.name}</div>
                <div className='uncompleted-course-card__meta'>
                    <span className='uncompleted-course-card__meta-item'>
                        <img className='mg-0' src='/images/icon2.svg' alt=''
                            style={{ height: '13px', width: '13px' }} />
                        {course.totalLecture ?? 0} コンテンツ
                    </span>
                    <span className='uncompleted-course-card__meta-item'>
                        <img className='mg-0' src='/images/icon1.svg' alt=''
                            style={{ height: '14px', width: '14px' }} />
                        約 {convertVideoDuration(course.totalTimeCourse)} 分
                    </span>
                    {course.incentivePeriod && (
                        <span className='uncompleted-course-card__meta-item'>
                            <span className='label'>受講推奨期限:</span>
                            <span className='value value--info'>
                                {moment(course.incentivePeriod).format(DATE_YMD)}
                            </span>
                        </span>
                    )}
                    {course.deadline && (
                        <span className='uncompleted-course-card__meta-item is-last'>
                            <span className='label'>受講期限:</span>
                            <span className='value value--danger'>
                                {moment(course.deadline).format(DATE_YMD)}
                            </span>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
});

export default UnCompletedCourseTab;